import {
    type FallbackNs,
    useTranslation as baseUseTranslation,
    type UseTranslationOptions,
    type UseTranslationResponse,
} from 'react-i18next';
import { type FlatNamespace, type KeyPrefix, type TFunction } from 'i18next';
import { type $Tuple } from 'node_modules/react-i18next/helpers';

import { useContextData } from '@common/useContextData';

/**
 * @description A wrapper around the react-i18next useTranslation hook that provides
 * platform-specific translation fallback logic. For a given translation key, it first
 * attempts to find a platform-prefixed version (e.g., platformID.keyName). If not found,
 * it falls back to the generic key without the prefix. If neither translation exists,
 * it automatically creates the missing translation key in lokalize without the prefix.
 * @note Keys containing namespaces (with colon separator, e.g., namespace:key) are
 * handled directly without platform prefixing.
 * @returns The UseTranslationResponse object from react-i18next
 */
export function useTranslation<
    const Ns extends FlatNamespace | $Tuple<FlatNamespace> | undefined = undefined,
    const KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
>(ns?: Ns, options?: UseTranslationOptions<KPrefix>): UseTranslationResponse<FallbackNs<Ns>, KPrefix> {
    const translation = baseUseTranslation(ns, options);
    const {
        platform: { id },
    } = useContextData();

    const t: TFunction<FallbackNs<Ns>, KPrefix> = ((
        givenKey: string | string[],
        subOptions: Record<string, string>,
    ): string => {
        const [key] = [givenKey].flat();

        if (key && containsNameSpace(key)) {
            return translation.t(key, subOptions);
        }

        const keyWithPlatformIDPrefix = `${id}.${key || ''}`;
        const value = translation.t(keyWithPlatformIDPrefix, subOptions);
        return value !== keyWithPlatformIDPrefix ? value : translation.t(key || '', subOptions);
    }) as TFunction<FallbackNs<Ns>, KPrefix>;

    return { ...translation, t };
}

const containsNameSpace = (key: string) => key.includes(':');
